import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { version } from 'src/environments/version';
import { HelperService } from '../../helper.service';
import { BugReportDiagnostics } from '../bug-report.model';
import { ErrorBufferService } from './error-buffer.service';

/**
 * Everything the app can say about itself without asking the user a single question.
 *
 * That is the point of the feature: a reporter describes what they saw, and the app supplies the
 * context they would otherwise be asked for in a follow-up email. All of it is best-effort. A
 * report that loses one field is far better than one that is never sent, so every read here is
 * wrapped and every field is optional.
 *
 * What is deliberately NOT collected: anything from sessionStorage beyond the identifiers below.
 * `sessionStorage.token` is a live key in this app and `sessionStorage.user` holds a serialised
 * user object. Identifiers correlate a report to a person who is already identified by the access
 * token; the stored objects would only add personal information the Back Office did not need.
 */
@Injectable({ providedIn: 'root' })
export class DiagnosticsService {
  private readonly router = inject(Router);
  private readonly helper = inject(HelperService);
  private readonly buffer = inject(ErrorBufferService);

  collect(): BugReportDiagnostics {
    const { consoleErrors, httpFailures } = this.buffer.snapshot();

    return prune({
      appVersion: version.commit === 'unknown' ? undefined : `${version.commit} (${version.build})`,
      brand: brandFromApiHost(),
      routeUrl: this.router.url,
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      language: navigator.language,
      timezone: quiet(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
      tenantId: numberOr(quiet(() => this.helper.getTenantId())),
      tenantName: stringOr(quiet(() => this.helper.getTenantName())),
      appUserId: numberOr(quiet(() => this.helper.getUserId())),
      appRoles: roles(),
      consoleErrors: consoleErrors.length > 0 ? consoleErrors : undefined,
      httpFailures: httpFailures.length > 0 ? httpFailures : undefined,
    });
  }

  /** The path alone. The API stores it separately from the full URL as `pagePath`. */
  pagePath(): string {
    return this.router.url.split('?')[0];
  }
}

/**
 * Derived from the admin API host rather than read from a field.
 *
 * `environmentName` exists in only three of the twelve environment files, so reading it would make
 * this blank on every deployed build. The host the app is configured to talk to identifies the
 * brand unambiguously and cannot drift from the API it is reporting to, which is the property that
 * matters. The Back Office sets the authoritative `client` and `stage` from its own config
 * regardless; this is a cross-check, not the source of truth.
 */
function brandFromApiHost(): string | undefined {
  return quiet(() => {
    const host = new URL(environment.backofficeBaseUrl).hostname;
    if (host.includes('yaraallfarm')) return 'allfarm';
    if (host.includes('southern-gateway')) return 'southerngateway';
    if (host.includes('precisiongateway')) return 'precisiongateway';
    return undefined;
  });
}

function roles(): string[] | undefined {
  return quiet(() => {
    const raw = sessionStorage.getItem('roles');
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    const names = parsed
      .map((role) => (typeof role === 'string' ? role : (role as { name?: string })?.name))
      .filter((name): name is string => typeof name === 'string' && name.length > 0);
    // The server caps this at 50 and answers 400 above it, so trim rather than lose the report.
    return names.length > 0 ? names.slice(0, 50) : undefined;
  });
}

/** Any of these reads can throw on a half-initialised session. None is worth failing a report. */
function quiet<T>(read: () => T): T | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}

function numberOr(value: unknown): number | undefined {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isInteger(parsed) ? parsed : undefined;
}

function stringOr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Drops the fields that came back undefined.
 *
 * Written as a loop rather than with `Object.fromEntries`, which this project's TypeScript lib
 * target does not have. Not worth raising the target of the whole application for one line.
 */
function prune(diagnostics: BugReportDiagnostics): BugReportDiagnostics {
  const pruned: Record<string, unknown> = {};
  Object.keys(diagnostics).forEach((key) => {
    const value = (diagnostics as Record<string, unknown>)[key];
    if (value !== undefined) pruned[key] = value;
  });
  return pruned as BugReportDiagnostics;
}
